import StudentEdit from "@/controllers/student/student-edit";
import store from "@/store";
import axios from "axios";
import { maxLength } from "vuelidate/lib/validators";
import { withParams } from "vuelidate/lib";
import ValidationService from "@/services/ValidationService";
import CONFIG from "@/assets/js/config.custom.js";

const inZoneOptions = withParams(
  { type: "inZoneOptions" },
  function (value) {
    if (value === 0 || value === null || value === "" || typeof value === "undefined") {
      return true;
    }

    return this.zoneArr.some((zone) => Number(zone.id) === Number(value));
  }
);

export default {
  extends: StudentEdit,
  mixins: [ValidationService],
  data() {
    return {
      academicYearArr: [],
      selectAcdYear: 0,
      referredByStudentNo: "",
      referrerInfo: null,
      referrerLookupError: "",
      isReferredByLocked: false,
      isInitializing: true,
      url: '/class/create?rd_back=student',
      zoneArr: [{
        id: 0,
        text: "Select Zone"
      }],
      selectedZone: 0,
      CONFIG,
      isUpdatingStatus: false,
      selectedStatus: 0,
    };
  },

  computed: {
    isAdmin() {
      return Number(store.state.user?.role) === CONFIG.ROLE.ID.ADMIN;
    },

    canChangeStudentStatus() {
      if (!this.studentData || typeof this.studentData.status === 'undefined') return false;
      const status = Number(this.studentData.status);
      return status === CONFIG.STUDENT_STATUS.FINANCIAL_SUSPENDED
        || status === CONFIG.STUDENT_STATUS.MISCONDUCT_SUSPENDED;
    },

    availableStatusOptions() {
      const currentStatus = Number(this.studentData.status);
      const options = [];

      if (currentStatus === CONFIG.STUDENT_STATUS.FINANCIAL_SUSPENDED) {
        options.push({
          value: CONFIG.STUDENT_STATUS.MISCONDUCT_SUSPENDED,
          text: CONFIG.STUDENT_STATUS_TEXT[CONFIG.STUDENT_STATUS.MISCONDUCT_SUSPENDED]
        });
        options.push({
          value: CONFIG.STUDENT_STATUS.ACTIVE,
          text: CONFIG.STUDENT_STATUS_TEXT[CONFIG.STUDENT_STATUS.ACTIVE]
        });
      } else if (currentStatus === CONFIG.STUDENT_STATUS.MISCONDUCT_SUSPENDED) {
        options.push({
          value: CONFIG.STUDENT_STATUS.ACTIVE,
          text: CONFIG.STUDENT_STATUS_TEXT[CONFIG.STUDENT_STATUS.ACTIVE]
        });
      }
      return options;
    },
  },

  watch: {
    selectAcdYear(acdYearId) {
      if (!this.isInitializing && acdYearId) {
        this.getGradeName(acdYearId);
      }
    },

    'mainClass.grade_id'() {
      if (this.isInitializing) return;

      this.noSectionMsg = "";
      this.sectionArr = [{
        id: 0,
        text: "Choose Section"
      }];
      this.mainClass.class_id = 0;
    },
  },

  validations: {
    ...StudentEdit.validations,
    referredByStudentNo: {
      maxLength: maxLength(20),
    },
    selectedZone: {
      inZoneOptions,
    }
  },

  methods: {
    async changeStudentStatus(newStatus) {
      if (this.isUpdatingStatus) return;
      const currentStatus = Number(this.studentData.status);
      const targetStatus = Number(newStatus);
      const allowed = [
        { from: CONFIG.STUDENT_STATUS.FINANCIAL_SUSPENDED, to: CONFIG.STUDENT_STATUS.MISCONDUCT_SUSPENDED },
        { from: CONFIG.STUDENT_STATUS.FINANCIAL_SUSPENDED, to: CONFIG.STUDENT_STATUS.ACTIVE },
        { from: CONFIG.STUDENT_STATUS.MISCONDUCT_SUSPENDED, to: CONFIG.STUDENT_STATUS.ACTIVE },
      ];
      if (!allowed.some(t => t.from === currentStatus && t.to === targetStatus)) {
        this.$notification.error('This status transition is not allowed.', {
          timer: 3,
          position: "bottomCenter"
        });
        return;
      }

      const confirmMessages = {
        [CONFIG.STUDENT_STATUS.MISCONDUCT_SUSPENDED]: 'Change status to Misconduct Suspended? The student will not be able to enter attendance.',
        [CONFIG.STUDENT_STATUS.ACTIVE]: 'Reactivate this student and set status to Active?',
      };

      if (!window.confirm(confirmMessages[targetStatus] || 'Update student status?')) {
        return;
      }

      this.isUpdatingStatus = true;
      const studentId = this.studentData.student_id ?? this.studentData.id;
      try {
        const response = await axios.post(`/student/change-status/${studentId}`, {
          new_status: targetStatus,
        });
        if (response.data && response.data.success) {
          this.studentData.status = targetStatus;
          sessionStorage.setItem('statusChangeSuccess', '1');
        }
      } catch (error) {
        let errorMessage = 'Failed to update student status.';
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        this.$notification.error(errorMessage, {
          timer: 3,
          position: "bottomCenter"
        });
      } finally {
        this.isUpdatingStatus = false;
      }
      window.location.replace('/student/list');
      return true;
    },

    async checkReferredByLock() {
      const studentId = Number(this.studentData.student_id ?? this.studentData.id);

      this.isReferredByLocked = false;

      if (!studentId) return;

      try {
        const { data } = await axios.get(`/referrals/check-referred-lock/${studentId}`);

        if (data && typeof data.locked === 'boolean') {
          this.isReferredByLocked = data.locked;
        }
      } catch (error) {
        if (error.response?.status === 401) {
          this.$store.commit("logout");
          window.location.replace("login");
        }
      }
    },

    lookupReferrer() {
      const studentNo = this.referredByStudentNo.trim();

      this.referrerInfo = null;
      this.referrerLookupError = "";

      if (!studentNo) {
        if (this.validationErrors.referred_by_student_no) {
          this.validationErrors.referred_by_student_no = [];
        }
        return Promise.resolve();
      }

      if (this.studentData?.c_student_no && studentNo === this.studentData.c_student_no.trim()) {
        if (this.validationErrors.referred_by_student_no) {
          this.validationErrors.referred_by_student_no = [];
        }
        this.referrerLookupError = "Student cannot refer themselves.";
        return Promise.resolve();
      }

      return axios
        .get(`student/getReferrerStudentName/${studentNo}`, {
        })
        .then((res) => {
          this.referrerInfo = res.data;
          if (this.validationErrors.referred_by_student_no) {
            this.validationErrors.referred_by_student_no = [];
          }
        })
        .catch((error) => {
          this.referrerInfo = null;
          if (this.validationErrors.referred_by_student_no) {
            this.validationErrors.referred_by_student_no = [];
          }
          this.referrerLookupError = error.response?.data?.message || "Unable to find referrer student.";
        });
    },

    getAcademicYears() {
      axios
        .get("period/get-current-next", {
          params: {
            campus_id: this.$store.state.user.campus_id,
          },
        })
        .then((res) => {
          this.academicYearArr = res.data;
        })
        .catch((err) => {
          if (err.response) {
            if (err.response.status == 401) {
              this.$store.commit("logout");
              window.location.replace("login");
            }
          }
        })
        .finally(() => (this.isLoading = false));
    },

    getZones() {
      axios
        .get("ferry-zone/list")
        .then((res) => {
          const options = res.data.map((zone) => ({
            id: zone.id,
            text: `${zone.zone_name} (${zone.ferry_price})`
          }));
          this.zoneArr = [{
            id: 0,
            text: "Select Zone"
          }].concat(options);
        })
        .catch((err) => {
          if (err.response && err.response.status == 401) {
            this.$store.commit("logout");
            window.location.replace("login");
          }
        });
    },

    getGradeName(selectAcdYear) {
      if (!selectAcdYear) return;

      axios
        .get("getGradeNameByAcademicYear", {
          params: {
            id: selectAcdYear,
            campus_id: this.$store.state.user.campus_id,
          },
        })
        .then((res) => {
          this.gradeArr = res.data;
          this.isLoading = false;
        })
        .catch((error) => {
          if (error.response.status == 401) {
            this.$store.commit("logout");
            window.location.replace("login");
          }
          this.isLoading = false;
        });
    },

    async getStudentEdit() {
      try {
        var request = {
          params: {
            campus_id: store.state.user.campus_id
          }
        }
        const response = await axios.get("/student/detail/" + this.$route.params.id, request)
        if (response.data) {
          this.studentData = response.data;
          this.selectedZone = this.studentData.zone_id || null;
          this.referredByStudentNo = this.studentData.referred_by_student_no || "";
          await this.checkReferredByLock();
          if (this.referredByStudentNo) {
            await this.lookupReferrer();
          }
          this.student_image = this.studentData.profile_path;
          this.grade = this.studentData.grade_id;
          if (Array.isArray(this.studentData.classes) && this.studentData.classes.length > 0) {
            this.mainClass = this.studentData.classes[0] || {};
            this.classes = this.studentData.classes.slice(1);
          } else {
            this.mainClass = { grade_id: 0, class_id: 0, annual_fee: 0, monthly_fee: 0, section: '' };
            this.classes = [];
          }
          this.selectedReligion = this.religion = this.studentData.religion;

          var request1 = {
            params: {
              campus_id: store.state.user.campus_id,
              academic_year: this.studentData.acd_year_id
            }
          }
          const mainGradeId = this.mainClass && this.mainClass.grade_id ? this.mainClass.grade_id : 0;
          const mainClassId = this.mainClass && this.mainClass.class_id ? this.mainClass.class_id : 0;
          this.getSection(mainGradeId, request1, mainClassId, 'main');
          this.calculateFee();
          this.getParentDetail(this.studentData.parent_id);
          this.getGradeName();
        }

      } catch (error) {
        console.log(error);
        if (error.status == 401) {
          this.$store.commit('logout');
          window.location.replace('login');
        }
        this.isLoading = false;
        console.log("Student Detail API Error", error);
      }
    },

    confirmUpdate() {
      if (this.disabled) return;
      this.submitted = true;
      if (this.$v && this.$v.studentData && this.$v.studentData.$invalid) {
        return;
      }
      if (this.isAdmin) {
        const studentNo = this.referredByStudentNo.trim();
        if (
          studentNo &&
          this.studentData?.c_student_no &&
          studentNo === this.studentData.c_student_no.trim()
        ) {
          if (this.validationErrors.referred_by_student_no) {
            this.validationErrors.referred_by_student_no = [];
          }
          this.referrerLookupError = "Student cannot refer themselves.";
          return;
        }
        if (this.referrerLookupError) {
          const studentNo = this.referredByStudentNo.trim();
          if (!studentNo) {
            this.referrerLookupError = "";
          } else {
            return;
          }
        }
      }
      this.update();
    },

    async update() {
      this.disabled = true;
      this.validationErrors = {};

      if (Number(this.selectedStatus) !== Number(this.studentData.status)) {
        await this.changeStudentStatus(this.selectedStatus);
        this.disabled = false;
        return;
      }
      this.isLoading = true;

      const combinedClasses = [this.mainClass, ...this.classes];
      const request = {
        parent_id: this.parentData ? this.parentData.id : null,
        name: this.studentData.name,
        student_image: this.student_image,
        student_image_old: this.studentData.profile_path,
        c_student_no: this.studentData.c_student_no,
        dob: this.studentData.dob,
        entry_no: this.studentData.entry_no,
        gender: this.studentData.gender,
        result: this.studentData.result,
        nationality: this.studentData.nationality,
        religion: this.religion,
        weight: this.studentData.weight,
        phone1: this.studentData.phone1,
        phone2: this.studentData.phone2,
        password: this.pwdData.password,
        blood: this.studentData.blood,
        height: this.studentData.height,
        sibling: this.studentData.sibling,
        hobby: this.studentData.hobby,
        allergic_food: this.studentData.allergic_food,
        previous_school: this.studentData.previous_school,
        special_topic: this.studentData.special_topic,
        campus_id: store.state.user.campus_id,
        ferry_status: this.studentData.ferry_status,
        zone_id: Number(this.selectedZone) || null,
        updated_id: store.state.user.id,
        academic_year: this.selectAcdYear,
        ...(this.isAdmin && { referred_by_student_no: this.referredByStudentNo }),
        classes: combinedClasses,
      };

      axios.put("/student/update/" + this.$route.params.id, request, {
        headers: {
          "Content-Type": "application/json",
        }
      })
        .then(response => {
          if (response.data.success == true) {
            this.$notification.success("Student Updated Successfully", {
              timer: 3,
              position: "bottomCenter"
            });
            this.$router.go(-1);
          } else {
            this.disabled = false;
            this.$notification.error(response.data.message, {
              timer: 3,
              position: "bottomCenter"
            });
          }
        })
        .catch(error => {
          this.printValidationError(error.response);

          if (error.response.status == 401) {
            this.$store.commit("logout");
            window.location.replace("login");
          }
          this.isLoading = false;
          this.disabled = false;

          if (error.response.status == 422) {
            return;
          }

          this.$notification.error("Unable to update student. Please Try Again", {
            timer: 3,
            position: "bottomCenter"
          });
          console.log("Student Update API Error", error.response.data.message);
        });
    },

    changeAcademicYear(acdYearId) {
      if (acdYearId && acdYearId > 0) {
        this.getGradeName(acdYearId);
        this.mainClass.grade_id = 0;
        this.mainClass.class_id = 0;
        this.sectionArrMain = [{ id: 0, text: "Choose Section" }];
        this.mainClass.annual_fee = 0;
        this.mainClass.monthly_fee = 0;
      }
    },

    changeEvent(val, fieldName, type = null) {
      this.$set(this, fieldName, val);
      this.attendedClass = false;
      if (fieldName == 'grade') {
        this.inputData.class_id = null;
        this.sectionArr = [{
          id: 0,
          text: 'Choose Section'
        }];
        var request = {
          params: {
            campus_id: store.state.user.campus_id,
            is_edit: true,
            academic_year: this.selectAcdYear,
          }
        }

        if (type == 'main' && val != 0) {
          this.attendedClassMain = this.classes.some(obj => obj.grade_id == this.mainClass.grade_id);
        }

        if (val == 0 && type == 'main') {
          this.attendedClassMain = false;
          this.sectionArrMain = [{
            "id": 0,
            "text": "Choose Section"
          }];
          this.noSectionMsg = '';
          this.isLoading = false;
        } else if (val == 0 && type == null) {
          this.sectionArr = [{
            "id": 0,
            "text": "Choose Section"
          }];
          this.noSectionMsg = '';
          this.isLoading = false;
          this.gradeFeeData = { 'annual_fee': '', 'monthly_fee': '' };
        } else {
          this.getSection(val, request, 0, type);
          this.getGradeFee(val, request, type);
        }
      }

      if (fieldName == 'section' && type == 'main') {
        this.mainClass.class_id = val;
        this.mainClass.section = this.sectionArrMain.find(e => e.id == val)?.text || '';
      } else if (fieldName == 'section' && type == null) {
        this.inputData.class_id = val;
        this.inputData.section = this.sectionArr.find(e => e.id == val)?.text || '';
      }
    },

    async initializeStudentData() {
      const academicYearId = this.studentData.acd_year_id;
      if (academicYearId) {
        this.selectAcdYear = academicYearId;
        await this.getGradeName(academicYearId);

        const mainGradeId = this.mainClass?.grade_id || 0;
        const mainClassId = this.mainClass?.class_id || 0;

        if (mainGradeId > 0) {
          var request = {
            params: {
              campus_id: store.state.user.campus_id,
              academic_year: academicYearId
            }
          };
          this.getSection(mainGradeId, request, mainClassId, 'main');
        }
      }
    },
  },

  async mounted() {
    this.isLoading = true;
    await this.getAcademicYears();
    await this.getZones();
    await this.getStudentEdit();
    this.selectedStatus = this.studentData.status;
    if ((Number(this.studentData.status) === CONFIG.STUDENT_STATUS.FINANCIAL_SUSPENDED || Number(this.studentData.status) === CONFIG.STUDENT_STATUS.MISCONDUCT_SUSPENDED) && this.availableStatusOptions.length > 0) {
      this.selectedStatus = this.availableStatusOptions[0].value;
    }
    await this.initializeStudentData();
    this.isInitializing = false;
  },
};
